'use client'

import { Accessibility } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'

import { useAccessibility } from './AccessibilityProvider'
import { ColorSection } from './sections/ColorSection'
import { FocusSection } from './sections/FocusSection'
import { MotionSection } from './sections/MotionSection'
import { NavigationSection } from './sections/NavigationSection'
import { ReadingSection } from './sections/ReadingSection'
import { TextSection } from './sections/TextSection'

export function AccessibilityToolbar() {
  const [open, setOpen] = useState(false)
  const { reset, authenticated } = useAccessibility()

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.altKey && e.shiftKey && (e.key === 'A' || e.key === 'a')) {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {/* Okidač nosi vidljiv tekst, ne samo ikonu — naziv opisuje svrhu ("Prikaz i
            čitanje"), a ne apstraktnu "pristupačnost". Na mobilnom je pill niži i
            uži, ali tekst ostaje. aria-label sadrži vidljivi tekst (WCAG 2.5.3) i
            dodaje prečicu. */}
        <Button
          variant="default"
          aria-label="Prikaz i čitanje (Alt+Shift+A)"
          className="fixed bottom-6 right-6 z-40 h-12 gap-2 rounded-full px-4 shadow-lg sm:h-14 sm:px-5"
        >
          <Accessibility className="!h-5 !w-5 sm:!h-6 sm:!w-6" aria-hidden="true" />
          <span className="text-xs font-medium sm:text-sm">Prikaz i čitanje</span>
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-6 overflow-y-auto sm:max-w-md"
        aria-describedby="a11y-description"
      >
        <SheetHeader>
          <SheetTitle>Prikaz i čitanje</SheetTitle>
          {/* Bez naloga podešavanja žive samo u ovom pregledaču (localStorage +
              cookie) — tvrdnja o nalogu bi za goste bila netačna. */}
          <SheetDescription id="a11y-description">
            Prilagodi kako sadržaj izgleda i kako se ponaša. Promene se čuvaju{' '}
            {authenticated ? 'na tvom nalogu i prate te na svim uređajima.' : 'na ovom uređaju.'}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-8">
          <TextSection />
          <ColorSection />
          <MotionSection />
          <ReadingSection />
          <FocusSection />
          <NavigationSection />
        </div>

        <div className="mt-auto border-t pt-4">
          <Button variant="outline" onClick={reset} className="w-full">
            Vrati sve na podrazumevano
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
